import CubeIndex from '../cube/CubeIndex';
import {itemColor} from '../render/colors';
import {respondents} from '../render/format';
import {isLowBase} from '../render/lowBase';
import {Cube, CubeCellValue, CubeMeasure, MarkRenderer, RenderContext, RenderSpec} from '../types';
import createMapView from './map/olMapView';

export interface MapFeatureRow {
    measure: CubeMeasure;
    cell: CubeCellValue | undefined;
}

export interface MapFeature {
    key: string;
    label: string;
    color: string;
    /** One position is a point, two or more an area (the v1 rule) */
    kind: 'point' | 'area';
    /** [lng, lat] */
    coords: [number, number][];
    lowBase: boolean;
    rows: MapFeatureRow[];
}

export interface MapModel {
    features: MapFeature[];
    /** Real members that have no position, so they cannot be drawn */
    skipped: number;
}

export function mapModel(cube: Cube, spec: RenderSpec, ctx: RenderContext): MapModel {
    const index = new CubeIndex(cube);
    const channel = typeof spec.encoding.location === 'string' ? spec.encoding.location : undefined;
    const features: MapFeature[] = [];
    let skipped = 0;

    index.items(channel).forEach(({member, index: position}) => {
        if (!member || member.role) {
            return; // total and missing have no place on a map
        }
        if (!member.geo?.length) {
            skipped++;
            return;
        }
        const rows = cube.measures.map(measure => ({measure, cell: index.value({[channel!]: position}, measure.id)}));
        features.push({
            key: member.key,
            label: member.label,
            color: itemColor(member, undefined, position, ctx),
            kind: member.geo.length === 1 ? 'point' : 'area',
            coords: member.geo,
            lowBase: rows.some(row => isLowBase(cube, row.cell)),
            rows,
        });
    });

    return {features, skipped};
}

/**
 * Totals of several features per measure: counts add up, a share is weighted by base, a value by count (sums of
 * weights on a weighted project, whose respondents add up under n).
 * Exact only for additive values (same approximation as v1); respondents in several options count twice.
 */
export function clusterSummary(features: MapFeature[], measures: CubeMeasure[]): MapFeatureRow[] {
    return measures.map(measure => {
        const cells = features
            .map(feature => feature.rows.find(row => row.measure.id === measure.id)?.cell)
            .filter((cell): cell is CubeCellValue => !!cell && cell.value !== null);
        const count = cells.reduce((sum, cell) => sum + cell.count, 0);
        const weight = (cell: CubeCellValue): number => measure.metric === 'share' ? cell.base ?? cell.count : cell.count;
        const total = cells.reduce((sum, cell) => sum + weight(cell), 0);

        let value: number | null = null;
        if (cells.length) {
            if (measure.metric === 'count') {
                value = cells.reduce((sum, cell) => sum + (cell.value as number), 0);
            } else if (total > 0) {
                value = cells.reduce((sum, cell) => sum + (cell.value as number) * weight(cell), 0) / total;
            }
        }

        const weighted = cells.some(cell => cell.n !== undefined);
        const n = cells.reduce((sum, cell) => sum + respondents(cell), 0);

        return {measure, cell: {value, count, ...(weighted ? {n} : {})}};
    });
}

const renderMap: MarkRenderer = (cube, spec, ctx) => {
    const model = mapModel(cube, spec, ctx);

    return {type: 'html', render: el => createMapView(el, model, cube, spec.options, ctx)};
};

export default renderMap;
